
// Flow:
//   1. Wait for initAuth() to complete (prevents race condition on page refresh)
//   2. Public routes → allow (redirect authenticated users away from login/register)
//   3. Not authenticated → redirect to /login with return URL
//   4. Wrong role → redirect to /unauthorized
//   5. All checks pass → allow

import type { Router } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

export function setupGuards(router: Router): void {
  router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore()

    // Step 1: Wait for session restoration
    // [W1-01] Only block navigation if we're heading to a protected route 
    // or if we have an active session to restore (to handle redirects).
    const hasSession = localStorage.getItem('isAuthenticated') === 'true'
    const isProtectedRoute = to.meta.public !== true

    if (!authStore.isInitialized && (hasSession || isProtectedRoute)) {
      await authStore.initAuth()
    }

    // Step 2: Public routes — allow immediately if initialization is still in progress
    // (except for login/register which need to know auth status to redirect away)
    if (to.meta.public === true) {
      const authRoutes = ['login', 'register', 'verify-email', 'forgot-password', 'reset-password']
      
      // If we're not initialized yet and it's not a sensitive auth route, allow guest view
      if (!authStore.isInitialized && !authRoutes.includes(to.name as string)) {
        return next()
      }

      // If initialized, redirect authenticated users away from login/register
      if (authStore.isAuthenticated && authRoutes.includes(to.name as string)) {
        return next(authStore.homeRoute)
      }
      return next()
    }

    // Step 3: Not authenticated — go to login 
    if (!authStore.isAuthenticated) {
      return next({ name: 'login', query: { redirect: to.fullPath } })
    }

    // Step 4: Role check 
    const requiredRole = (to.meta.role ?? to.matched.find(r => r.meta.role)?.meta.role) as string | undefined
    if (requiredRole && authStore.role !== requiredRole) {
      return next({ name: 'unauthorized' })
    }

    // Step 5: Email verification check for candidate routes
    if (requiredRole === 'candidate' && !authStore.isEmailVerified) {
      return next({ name: 'verify-email' })
    }

    next()
  })

}
